import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";
import { Info, ShieldCheck, School, Users, KeyRound, ArrowRight, Trash2, AlertTriangle, ShieldAlert, Upload, FileSpreadsheet, Download, Activity, GraduationCap, BellRing } from "lucide-react";
import { toast } from "sonner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [pwOpen, setPwOpen] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [schoolWipeOpen, setSchoolWipeOpen] = useState(false);
  const [schoolConfirmChecked, setSchoolConfirmChecked] = useState(false);
  const [schoolConfirmText, setSchoolConfirmText] = useState("");
  const [fundWipeOpen, setFundWipeOpen] = useState(false);
  const [fundConfirmChecked, setFundConfirmChecked] = useState(false);
  const [fundConfirmText, setFundConfirmText] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSheet, setImportSheet] = useState("RDM25");
  const [importWipe, setImportWipe] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const canManageUsers = ["admin", "direzione"].includes(user?.role);
  const canWipeDemo = ["admin", "direzione"].includes(user?.role);
  const canWipeSchool = user?.role === "admin";
  const canImport = ["admin", "direzione"].includes(user?.role);

  const { data: demoStats } = useQuery({
    queryKey: ["admin-fundraising-demo-preview"],
    queryFn: async () => (await api.get("/admin/fundraising-demo/preview")).data,
    enabled: canWipeDemo,
  });

  const wipeMut = useMutation({
    mutationFn: async () => (await api.post("/admin/fundraising-demo/wipe")).data,
    onSuccess: (d) => {
      const del = d.deleted || {};
      const total = Object.values(del).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Dati demo eliminati (${total} record)`);
      qc.invalidateQueries({ queryKey: ["admin-fundraising-demo-preview"] });
      qc.invalidateQueries({ queryKey: ["donors"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setWipeOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore durante l'eliminazione"),
  });

  const importMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("sheet_name", importSheet || "RDM25");
      fd.append("wipe_before", String(importWipe));
      return (await api.post("/admin/import-rdm-excel", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      setImportResult(d);
      toast.success(`Import completato: ${d.payments_created} pagamenti (${d.total_amount} €)`);
      qc.invalidateQueries({ queryKey: ["donors"] });
      qc.invalidateQueries({ queryKey: ["payments-page"] });
      qc.invalidateQueries({ queryKey: ["payments-kpi"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore import"),
  });

  const restoreMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("wipe_before", "true");
      return (await api.post("/admin/restore-json", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      setRestoreResult(d);
      const totRestored = Object.values(d.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restore completato: ${totRestored} record ripristinati`);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore restore"),
  });

  const { data: schoolStats } = useQuery({
    queryKey: ["admin-school-data-preview"],
    queryFn: async () => (await api.get("/admin/school-data/preview")).data,
    enabled: canWipeSchool,
  });

  const schoolWipeMut = useMutation({
    mutationFn: async () => (await api.post("/admin/school-data/wipe", {
      understood: schoolConfirmChecked,
      confirmation_phrase: schoolConfirmText,
    })).data,
    onSuccess: (d) => {
      const del = d.deleted || {};
      const total = Object.values(del).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Area Scuola azzerata (${total} record eliminati)`);
      qc.invalidateQueries({ queryKey: ["admin-school-data-preview"] });
      qc.invalidateQueries();
      setSchoolWipeOpen(false);
      setSchoolConfirmChecked(false);
      setSchoolConfirmText("");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore durante l'eliminazione"),
  });

  const schoolCanSubmit = schoolConfirmChecked && schoolConfirmText.trim() === "ELIMINA DATI SCUOLA";

  const { data: fundStats } = useQuery({
    queryKey: ["admin-fundraising-data-preview"],
    queryFn: async () => (await api.get("/admin/fundraising-data/preview")).data,
    enabled: canWipeSchool,
  });

  const fundWipeMut = useMutation({
    mutationFn: async () => (await api.post("/admin/fundraising-data/wipe", {
      understood: fundConfirmChecked,
      confirmation_phrase: fundConfirmText,
    })).data,
    onSuccess: (d) => {
      const del = d.deleted || {};
      const total = Object.values(del).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Raccolta fondi azzerata (${total} record eliminati)`);
      qc.invalidateQueries();
      setFundWipeOpen(false);
      setFundConfirmChecked(false);
      setFundConfirmText("");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore durante l'eliminazione"),
  });

  const fundCanSubmit = fundConfirmChecked && fundConfirmText.trim() === "ELIMINA RACCOLTA FONDI";

  const demoTotal = demoStats
    ? Object.values(demoStats).reduce((a, b) => a + (b || 0), 0)
    : 0;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader title="Impostazioni" subtitle="Gestione account, utenti e integrazioni."/>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium flex items-center gap-2"><ShieldCheck size={16}/> Account</div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">Nome</span><span className="font-medium">{user?.name}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Email</span><span className="font-medium">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Ruolo</span><span className="font-medium capitalize">{user?.role}</span></div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-4 rounded-full border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white"
            onClick={() => setPwOpen(true)}
            data-testid="settings-change-pw"
          >
            <KeyRound size={13} className="mr-1.5"/> Cambia password
          </Button>
        </Card>

        {canManageUsers && (
          <Link to="/impostazioni/utenti">
            <Card
              data-testid="settings-users-card"
              className="p-6 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors cursor-pointer h-full"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2 text-[#1C352D]"><Users size={16}/> Gestione utenti</div>
                  <div className="text-xs text-stone-500 mt-2 leading-relaxed">
                    Crea e gestisci account amministratori, segreteria, docenti e studenti. Assegna ruoli e privilegi.
                  </div>
                </div>
                <ArrowRight size={16} className="text-stone-400"/>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {["Amministratore", "Direzione", "Segreteria", "Docente", "Studente"].map((r) => (
                  <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{r}</span>
                ))}
              </div>
            </Card>
          </Link>
        )}

        {user?.role === "admin" && (
          <Link to="/impostazioni/permessi">            <Card
              data-testid="settings-permessi-card"
              className="p-6 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors cursor-pointer h-full"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2 text-[#1C352D]"><ShieldCheck size={16}/> Permessi utenti</div>
                  <div className="text-xs text-stone-500 mt-2 leading-relaxed">
                    Personalizza a quali moduli e sotto-sezioni ha accesso ciascun utente. Solo admin.
                  </div>
                </div>
                <ArrowRight size={16} className="text-stone-400"/>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {["Raccolta fondi", "Scuola", "Amministrazione", "Mansioni", "Segreteria"].map((r) => (
                  <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{r}</span>
                ))}
              </div>
            </Card>
          </Link>
        )}

        {canManageUsers && (
          <Link to="/impostazioni/scuola">
            <Card
              data-testid="settings-school-card"
              className="p-6 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors cursor-pointer h-full"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2 text-[#1C352D]"><GraduationCap size={16}/> Scuola · Target CFU & Monte ore</div>
                  <div className="text-xs text-stone-500 mt-2 leading-relaxed">
                    Imposta il monte ore permessi di default per gli studenti e i target CFU per anno/percorso (con override manuale sul catalogo).
                  </div>
                </div>
                <ArrowRight size={16} className="text-stone-400"/>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {["Monte ore permessi", "CFU per anno", "Target diploma"].map((r) => (
                  <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{r}</span>
                ))}
              </div>
            </Card>
          </Link>
        )}

        {canManageUsers && (
          <Link to="/impostazioni/notifiche">
            <Card
              data-testid="settings-staff-notify-card"
              className="p-6 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors cursor-pointer h-full"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2 text-[#1C352D]"><BellRing size={16}/> Notifiche staff</div>
                  <div className="text-xs text-stone-500 mt-2 leading-relaxed">
                    Scegli chi riceve push ed email per ogni evento: permessi, richieste, messaggi, ticket, documenti degli studenti.
                  </div>
                </div>
                <ArrowRight size={16} className="text-stone-400"/>
              </div>
            </Card>
          </Link>
        )}

        {canManageUsers && (
          <Link to="/impostazioni/health">
            <Card
              data-testid="settings-health-card"
              className="p-6 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors cursor-pointer h-full"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2 text-[#1C352D]"><Activity size={16}/> Stato del sistema</div>
                  <div className="text-xs text-stone-500 mt-2 leading-relaxed">
                    Diagnostica live: URL pubblico, database, invio email (Brevo), push notification, dati base. Utile prima di segnalare un bug.
                  </div>
                </div>
                <ArrowRight size={16} className="text-stone-400"/>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {["APP URL", "MongoDB", "Brevo", "Web Push"].map((r) => (
                  <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{r}</span>
                ))}
              </div>
            </Card>
          </Link>
        )}
          <Card
            data-testid="settings-import-card"
            className="p-6 border border-emerald-200 bg-emerald-50/30 rounded-xl shadow-none lg:col-span-2"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium flex items-center gap-2 text-emerald-900">
                  <FileSpreadsheet size={16}/> Import contabilità RDM (Excel)
                </div>
                <p className="text-xs text-stone-600 mt-2 leading-relaxed max-w-3xl">
                  Carica il file <code className="mono text-[11px]">CONTABILITA' RAMODIMANDORLO.xlsx</code> per importare
                  donatori e pagamenti in massa dal foglio RDM25/RDM26. I donatori esistenti (matcha su CF)
                  vengono riusati, non duplicati. Utile in produzione al primo caricamento e quando ricevi
                  l'Excel aggiornato dalla contabilità.
                </p>
              </div>
              <Button
                data-testid="settings-import-btn"
                onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <Upload size={14} className="mr-1.5"/> Importa Excel
              </Button>
            </div>
          </Card>
        )}

        {canImport && (
          <Card
            data-testid="settings-restore-card"
            className="p-6 border border-sky-200 bg-sky-50/30 rounded-xl shadow-none lg:col-span-2"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium flex items-center gap-2 text-sky-900">
                  <Upload size={16}/> Migrazione one-shot (preview ↔ produzione)
                </div>
                <p className="text-xs text-stone-600 mt-2 leading-relaxed max-w-3xl">
                  <b>Passo 1 (nel preview)</b>: clicca <b>Scarica JSON</b> per esportare lo stato attuale (donatori,
                  pagamenti, impegni, scadenze).<br/>
                  <b>Passo 2 (in produzione)</b>: torna in Impostazioni, clicca <b>Restore JSON</b> e carica il file scaricato.
                  Il restore fa <b>wipe automatico</b> delle collezioni target e ripristina 1-a-1.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid="settings-export-json-btn"
                  onClick={async () => {
                    try {
                      const res = await api.get("/admin/export-fundraising-json", { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `rdm_fundraising_export_${new Date().toISOString().slice(0,10)}.json`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      toast.success("Export scaricato");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Errore export");
                    }
                  }}
                  className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  <Download size={14} className="mr-1.5"/> Scarica JSON
                </Button>
                <Button
                  data-testid="settings-restore-btn"
                  onClick={() => { setRestoreOpen(true); setRestoreResult(null); setRestoreFile(null); }}
                  className="rounded-full bg-sky-700 hover:bg-sky-800 text-white"
                >
                  <Upload size={14} className="mr-1.5"/> Restore JSON
                </Button>
              </div>
            </div>
          </Card>
        )}

        {canWipeDemo && (
          <Card
            data-testid="settings-wipe-demo-card"
            className="p-6 border border-amber-200 bg-amber-50/40 rounded-xl shadow-none lg:col-span-2"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium flex items-center gap-2 text-amber-900">
                  <AlertTriangle size={16}/> Pulizia dati demo · Raccolta fondi
                </div>
                <p className="text-xs text-stone-600 mt-2 leading-relaxed max-w-2xl">
                  Elimina tutti i donatori dimostrativi (marcati come <code className="mono text-[11px]">__demo__</code>) e i relativi
                  pledge, scadenze, pagamenti, comunicazioni e follow-up. I donatori reali che aggiungerai <b>non</b> vengono toccati.
                  L'azione è irreversibile.
                </p>
                {demoStats && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(demoStats).map(([k, v]) => (
                      <span
                        key={k}
                        className="text-[10px] px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-900"
                      >
                        {k}: <b>{v}</b>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                data-testid="settings-wipe-demo-btn"
                onClick={() => setWipeOpen(true)}
                disabled={demoTotal === 0}
                className="rounded-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
              >
                <Trash2 size={14} className="mr-1.5"/>
                {demoTotal === 0 ? "Nessun dato demo" : "Elimina dati demo"}
              </Button>
            </div>
          </Card>
        )}

        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium flex items-center gap-2"><Info size={16}/> Integrazioni</div>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">Email (Brevo)</div>
                <div className="text-xs text-stone-500">Aggiungi <code className="mono">BREVO_API_KEY</code> in backend/.env per abilitare invio email/newsletter reali.</div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Da configurare</span>
            </li>
            <li className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">Object Storage (Ricevute)</div>
                <div className="text-xs text-stone-500">Attivo. Upload PDF/immagini fino a 10 MB.</div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Attivo</span>
            </li>
            <li className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">WhatsApp (Twilio)</div>
                <div className="text-xs text-stone-500">Preparato strutturalmente: i messaggi sono registrati nel timeline.</div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200">Fase 2</span>
            </li>
            <li className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">Pagamenti online (Stripe / PayPal)</div>
                <div className="text-xs text-stone-500">Metodi già selezionabili nei pagamenti manuali. Gateway attivabili in Fase 4.</div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200">Fase 4</span>
            </li>
          </ul>
        </Card>

        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium flex items-center gap-2"><School size={16}/> Modulo Scuola / Classroom</div>
          <p className="text-sm text-stone-600 mt-2">
            Il modulo scuola (studenti, docenti, corsi, presenze, esami, attestati) è progettato per integrarsi
            nella stessa base utenti e permessi.
          </p>
        </Card>

        {canWipeSchool && (
          <Card
            data-testid="settings-wipe-fundraising-card"
            className="p-6 border-2 border-red-300 bg-red-50/50 rounded-xl shadow-none lg:col-span-2"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold flex items-center gap-2 text-red-900">
                  <ShieldAlert size={16}/> Zona pericolo · Reset area Raccolta fondi
                </div>
                <p className="text-xs text-stone-700 mt-2 leading-relaxed max-w-3xl">
                  Elimina definitivamente <b>tutti</b> i dati della raccolta fondi: donatori (reali, demo e importati),
                  impegni, scadenze, pagamenti, comunicazioni e follow-up.
                  <br/><b>Non</b> tocca il modulo Scuola né gli account utente. Doppia conferma richiesta.
                </p>
                {fundStats && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(fundStats).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span
                        key={k}
                        className="text-[10px] px-2 py-1 rounded-full bg-white border border-red-200 text-red-900"
                      >
                        {k}: <b>{v}</b>
                      </span>
                    ))}
                    {Object.values(fundStats).every((v) => !v) && (
                      <span className="text-[10px] text-stone-500">Nessun dato da eliminare</span>
                    )}
                  </div>
                )}
              </div>
              <Button
                data-testid="settings-wipe-fundraising-btn"
                onClick={() => setFundWipeOpen(true)}
                disabled={!fundStats || Object.values(fundStats).every((v) => !v)}
                className="rounded-full bg-red-700 hover:bg-red-800 text-white disabled:opacity-40"
              >
                <Trash2 size={14} className="mr-1.5"/> Azzera raccolta fondi
              </Button>
            </div>
          </Card>
        )}

        {canWipeSchool && (
          <Card
            data-testid="settings-wipe-school-card"
            className="p-6 border-2 border-red-300 bg-red-50/50 rounded-xl shadow-none lg:col-span-2"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold flex items-center gap-2 text-red-900">
                  <ShieldAlert size={16}/> Zona pericolo · Reset area Scuola
                </div>
                <p className="text-xs text-stone-700 mt-2 leading-relaxed max-w-3xl">
                  Elimina definitivamente <b>tutti</b> i dati del modulo scuola: studenti, docenti, corsi, aree,
                  calendario, presenze, appelli, voti, materiali, richieste e thread messaggi.
                  <br/>Vengono cancellati anche gli account studenti e docenti (i restanti admin/direzione/segreteria restano attivi).
                  <br/><b>Non</b> tocca i dati della Raccolta fondi. Doppia conferma richiesta.
                </p>
                {schoolStats && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(schoolStats).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span
                        key={k}
                        className="text-[10px] px-2 py-1 rounded-full bg-white border border-red-200 text-red-900"
                      >
                        {k}: <b>{v}</b>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                data-testid="settings-wipe-school-btn"
                onClick={() => setSchoolWipeOpen(true)}
                className="rounded-full bg-red-700 hover:bg-red-800 text-white"
              >
                <Trash2 size={14} className="mr-1.5"/> Azzera area Scuola
              </Button>
            </div>
          </Card>
        )}
      </div>

      <AlertDialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi eliminazione dati demo?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                Verranno eliminati definitivamente tutti i record demo della Raccolta fondi:
                <ul className="mt-2 list-disc pl-5 text-xs space-y-0.5">
                  {demoStats && Object.entries(demoStats).map(([k, v]) => (
                    <li key={k}>{k}: <b>{v}</b></li>
                  ))}
                </ul>
                <div className="mt-3 text-xs text-stone-500">
                  I donatori reali (senza flag <code className="mono">__demo__</code>) restano intatti.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="settings-wipe-demo-confirm"
              onClick={() => wipeMut.mutate()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 size={13} className="mr-1.5"/> Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={fundWipeOpen} onOpenChange={(o) => { setFundWipeOpen(o); if (!o) { setFundConfirmChecked(false); setFundConfirmText(""); } }}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-800 flex items-center gap-2">
              <ShieldAlert size={18}/> Reset totale Raccolta fondi
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="text-sm text-stone-700">
                  Azione irreversibile. Elimina tutti i dati della raccolta fondi (donatori, impegni,
                  scadenze, pagamenti, comunicazioni, follow-up). Non tocca il modulo Scuola né gli utenti.
                </div>
                {fundStats && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="text-[11px] font-medium text-red-900 mb-1.5">Verranno eliminati:</div>
                    <ul className="text-xs space-y-0.5 grid grid-cols-2 gap-x-3">
                      {Object.entries(fundStats).filter(([, v]) => v > 0).map(([k, v]) => (
                        <li key={k} className="flex justify-between"><span className="text-stone-600">{k}</span><b className="text-red-900">{v}</b></li>
                      ))}
                    </ul>
                  </div>
                )}
                <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    data-testid="fund-wipe-confirm-check"
                    checked={fundConfirmChecked}
                    onCheckedChange={(v) => setFundConfirmChecked(!!v)}
                    className="mt-0.5"
                  />
                  <span>Ho compreso che questa operazione <b>non è reversibile</b> e che verranno persi tutti i dati donatori/pagamenti.</span>
                </label>
                <div className="text-sm">
                  Per confermare digita: <code className="mono text-[11px] px-1.5 py-0.5 bg-stone-100 border rounded">ELIMINA RACCOLTA FONDI</code>
                  <Input
                    data-testid="fund-wipe-confirm-input"
                    value={fundConfirmText}
                    onChange={(e) => setFundConfirmText(e.target.value)}
                    placeholder="ELIMINA RACCOLTA FONDI"
                    className="mt-2"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="fund-wipe-confirm-btn"
              disabled={!fundCanSubmit || fundWipeMut.isPending}
              onClick={(e) => { e.preventDefault(); if (fundCanSubmit) fundWipeMut.mutate(); }}
              className="bg-red-700 hover:bg-red-800 text-white disabled:opacity-40"
            >
              <Trash2 size={13} className="mr-1.5"/> {fundWipeMut.isPending ? "Eliminazione…" : "Azzera definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={schoolWipeOpen} onOpenChange={(o) => { setSchoolWipeOpen(o); if (!o) { setSchoolConfirmChecked(false); setSchoolConfirmText(""); } }}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-800 flex items-center gap-2">
              <ShieldAlert size={18}/> Reset totale area Scuola
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="text-sm text-stone-700">
                  Azione irreversibile. Elimina tutti i dati del modulo Scuola e gli account di studenti e docenti.
                </div>
                {schoolStats && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 max-h-40 overflow-y-auto">
                    <div className="text-[11px] font-medium text-red-900 mb-1.5">Verranno eliminati:</div>
                    <ul className="text-xs space-y-0.5 grid grid-cols-2 gap-x-3">
                      {Object.entries(schoolStats).filter(([, v]) => v > 0).map(([k, v]) => (
                        <li key={k} className="flex justify-between"><span className="text-stone-600">{k}</span><b className="text-red-900">{v}</b></li>
                      ))}
                    </ul>
                  </div>
                )}
                <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    data-testid="school-wipe-confirm-check"
                    checked={schoolConfirmChecked}
                    onCheckedChange={(v) => setSchoolConfirmChecked(!!v)}
                    className="mt-0.5"
                  />
                  <span>Ho compreso che questa operazione <b>non è reversibile</b> e che non esiste backup automatico.</span>
                </label>
                <div className="text-sm">
                  Per confermare digita: <code className="mono text-[11px] px-1.5 py-0.5 bg-stone-100 border rounded">ELIMINA DATI SCUOLA</code>
                  <Input
                    data-testid="school-wipe-confirm-input"
                    value={schoolConfirmText}
                    onChange={(e) => setSchoolConfirmText(e.target.value)}
                    placeholder="ELIMINA DATI SCUOLA"
                    className="mt-2"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="school-wipe-confirm-btn"
              disabled={!schoolCanSubmit || schoolWipeMut.isPending}
              onClick={(e) => { e.preventDefault(); if (schoolCanSubmit) schoolWipeMut.mutate(); }}
              className="bg-red-700 hover:bg-red-800 text-white disabled:opacity-40"
            >
              <Trash2 size={13} className="mr-1.5"/> {schoolWipeMut.isPending ? "Eliminazione…" : "Azzera definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} mustChange={false}/>

      <AlertDialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportResult(null); } }}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-emerald-700"/> Import Excel contabilità
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="text-sm text-stone-600">
                  Scegli il file .xlsx e il nome del foglio. Le date dei pagamenti vengono lette
                  automaticamente dall'header del foglio (riga 1) — metodo <b>bonifico</b>.
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="text-xs font-medium text-stone-700">File Excel (.xlsx)</label>
                    <input
                      data-testid="import-file-input"
                      type="file"
                      accept=".xlsx,.xlsm"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="mt-1 block w-full text-sm text-stone-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-emerald-700 file:text-white hover:file:bg-emerald-800"
                    />
                    {importFile && <div className="text-[11px] text-stone-500 mt-1">✓ {importFile.name} · {(importFile.size / 1024).toFixed(1)} KB</div>}
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="text-xs font-medium text-stone-700">Foglio</label>
                      <Input
                        data-testid="import-sheet"
                        value={importSheet}
                        onChange={(e) => setImportSheet(e.target.value)}
                        placeholder="RDM25"
                        className="mt-1"
                      />
                      <div className="text-[10px] text-stone-500 mt-1">Le date dei pagamenti vengono lette automaticamente dall'header del foglio (riga 1).</div>
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
                    <Checkbox
                      data-testid="import-wipe"
                      checked={importWipe}
                      onCheckedChange={(v) => setImportWipe(!!v)}
                      className="mt-0.5"
                    />
                    <span>Cancella prima gli import precedenti dello stesso anno. Utile per ri-import puliti dopo aggiornamento file.</span>
                  </label>
                </div>
                {importResult && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-1">
                    <div className="font-medium text-emerald-900">Import completato ✓</div>
                    <div>Donatori creati: <b>{importResult.donors_created}</b> · riusati: <b>{importResult.donors_reused}</b></div>
                    <div>Pagamenti creati: <b>{importResult.payments_created}</b></div>
                    <div>Totale importato: <b>€ {Number(importResult.total_amount).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b></div>
                    <div>Righe vuote ignorate: {importResult.skipped_empty_rows}</div>
                    {importResult.errors?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-amber-800">{importResult.errors.length} errori (clicca)</summary>
                        <ul className="mt-1 max-h-32 overflow-y-auto text-[10px] text-amber-900 pl-4 list-disc">
                          {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{importResult ? "Chiudi" : "Annulla"}</AlertDialogCancel>
            {!importResult && (
              <AlertDialogAction
                data-testid="import-confirm"
                disabled={!importFile || importMut.isPending}
                onClick={(e) => { e.preventDefault(); if (importFile) importMut.mutate(); }}
                className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-40"
              >
                <Upload size={13} className="mr-1.5"/> {importMut.isPending ? "Import in corso…" : "Avvia import"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={restoreOpen} onOpenChange={(o) => { setRestoreOpen(o); if (!o) { setRestoreResult(null); } }}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-sky-900">
              <Upload size={18}/> Restore JSON — Migrazione one-shot
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="text-sm text-stone-600">
                  Carica il file <code className="mono">preview_data.json</code> (esportato dal preview).
                  Ripristina donors, payments, pledges e schedules <b>sovrascrivendo</b> quelli esistenti.
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-700">File JSON (.json)</label>
                  <input
                    data-testid="restore-file-input"
                    type="file"
                    accept=".json"
                    onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                    className="mt-1 block w-full text-sm text-stone-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-sky-700 file:text-white hover:file:bg-sky-800"
                  />
                  {restoreFile && <div className="text-[11px] text-stone-500 mt-1">✓ {restoreFile.name} · {(restoreFile.size / 1024).toFixed(1)} KB</div>}
                </div>
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠️ <b>Azione distruttiva</b>: donors/payments/pledges/schedules attuali verranno cancellati.
                </div>
                {restoreResult && (
                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs space-y-1">
                    <div className="font-medium text-sky-900">Restore completato ✓</div>
                    {Object.entries(restoreResult.wiped || {}).map(([k, v]) => (
                      <div key={"w"+k}>Wipe {k}: <b>{v}</b></div>
                    ))}
                    {Object.entries(restoreResult.restored || {}).map(([k, v]) => (
                      <div key={"r"+k}>Ripristinati {k}: <b>{v}</b></div>
                    ))}
                    {restoreResult.skipped_empty?.length > 0 && (
                      <div className="text-stone-500">Collezioni assenti nel dump: {restoreResult.skipped_empty.join(", ")}</div>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{restoreResult ? "Chiudi" : "Annulla"}</AlertDialogCancel>
            {!restoreResult && (
              <AlertDialogAction
                data-testid="restore-confirm"
                disabled={!restoreFile || restoreMut.isPending}
                onClick={(e) => { e.preventDefault(); if (restoreFile) restoreMut.mutate(); }}
                className="bg-sky-700 hover:bg-sky-800 text-white disabled:opacity-40"
              >
                <Upload size={13} className="mr-1.5"/> {restoreMut.isPending ? "Restore in corso…" : "Avvia restore"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
